/**
 * Пригоди: things worth doing, and what you get for them.
 *
 * A short list rather than a tree. What each one asks for is *something in
 * your hands* - not a counter of everything you have ever picked up, not a
 * flag set by a place you walked through - and that is a deliberate
 * restriction rather than a simplification waiting to be replaced.
 *
 * The first quest is why. "Виростити та зібрати врожай льону" cannot be
 * checked by counting льон, because льон is not what comes off it: a ripe
 * crop gives **стебла**, and wild flax growing by itself gives seeds and
 * nothing else. So three стебла in a bag is proof that somebody tilled a
 * plot, sowed it, waited, and cut it - the whole of the task, evidenced by
 * an object. Nothing has to watch the player to know it happened.
 *
 * Where that stops working, this will need a tally of deeds instead. It has
 * not stopped working yet, and a tally is a second thing to save, to sync
 * and to get wrong.
 */

import type { Inventory } from "../inventory/inventory.js";
import type { Phrase } from "../text/lang.js";
import { PlainItemId } from "../block/items.js";

export interface Quest {
  readonly id: string;
  readonly name: Phrase;
  /** What to do, in a sentence. */
  readonly blurb: Phrase;
  /** What must be in the bag for it to count as done. */
  readonly want: { readonly item: number; readonly count: number };
  readonly reward: { readonly id: number; readonly count: number };
}

export const QUESTS: readonly Quest[] = [
  {
    id: "flax",
    name: { uk: "Перший льон", en: "The first flax" },
    blurb: {
      uk: "Виростити льон і зібрати три стебла. Дика рослина дає лише " +
        "насіння — стебла бувають тільки з грядки.",
      en: "Grow flax and cut three stalks. The wild plant gives seeds only - " +
        "stalks come off a sown plot and nowhere else.",
    },
    want: { item: PlainItemId.FlaxStalks, count: 3 },
    /*
     * An iron pickaxe for a first errand, which is a lot.
     *
     * It is meant to be. Growing flax is the first thing in this game that
     * takes *waiting* - you till, you sow, and then you go away and come
     * back - and the reward is the tool that opens the ground up. One pays
     * for the other: the patience is the price, and what you get is a reason
     * to go deeper.
     */
    reward: { id: PlainItemId.IronPickaxe, count: 1 },
  },
];

export function questById(id: string): Quest | undefined {
  return QUESTS.find((q) => q.id === id);
}

/** How much of what it asks for is in the bag, never more than it wants. */
export function questProgress(quest: Quest, inventory: Inventory): number {
  return Math.min(quest.want.count, inventory.countOf(quest.want.item));
}

/** Is it done but unclaimed? */
export function questReady(
  quest: Quest, inventory: Inventory, claimed: ReadonlySet<string>,
): boolean {
  return !claimed.has(quest.id) &&
    questProgress(quest, inventory) >= quest.want.count;
}

/** Why a claim was refused, or null when it was not. */
export type ClaimRefusal = "claimed" | "unfinished" | "full";

/**
 * Take the reward, and mark the quest done.
 *
 * The flax is **not** taken. What was asked for was growing it, and the
 * стебла are the evidence rather than the fee - a player who spent three
 * days on a plot so they could weave полотно should not find the game has
 * eaten it as payment for the pickaxe.
 *
 * A full bag refuses rather than dropping the reward on the floor or, worse,
 * marking the quest done and handing over nothing. `add` reports what did not
 * fit; if anything did not, what did fit is taken back out and the claim is
 * refused whole.
 */
export function claimQuest(
  quest: Quest,
  inventory: Inventory,
  claimed: Set<string>,
): ClaimRefusal | null {
  if (claimed.has(quest.id)) return "claimed";
  if (questProgress(quest, inventory) < quest.want.count) return "unfinished";

  const leftover = inventory.add(quest.reward.id, quest.reward.count);
  if (leftover > 0) {
    // Put back whatever did fit: a half-given reward is worse than none.
    const given = quest.reward.count - leftover;
    if (given > 0) inventory.take(quest.reward.id, given);
    return "full";
  }

  claimed.add(quest.id);
  return null;
}

/**
 * The claimed ids, cleaned of anything this build has never heard of.
 *
 * A save may name a quest that has since been renamed or dropped, and a
 * stranger's id sitting in the set forever is a small leak that shows up as
 * a quest nobody can claim. Read here, once, where it arrives.
 */
export function questsFromSave(ids: readonly string[] | undefined): Set<string> {
  const out = new Set<string>();
  for (const id of ids ?? []) {
    if (questById(id) !== undefined) out.add(id);
  }
  return out;
}
